import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, Chip, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, IconButton, Stack, Tooltip,
  CircularProgress, Alert
} from "@mui/material";
import { LocalOffer, Cancel, Visibility, PictureAsPdf } from "@mui/icons-material";
import API from "../services/api";
import toast from "react-hot-toast";

const trMoney = (x) => Number(x || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const trDate = (x) => x ? new Date(x).toLocaleDateString("tr-TR") : "—";
const trDateTime = (x) => x ? new Date(x).toLocaleString("tr-TR") : "—";

const STATUS_LABELS = {
  open:     { text: "Açık",         color: "warning" },
  priced:   { text: "Fiyatlandı",   color: "info"    },
  accepted: { text: "Kabul Edildi", color: "success" },
  rejected: { text: "Reddedildi",   color: "error"   },
  expired:  { text: "Süresi Doldu", color: "default" }
};

const TABS = [
  { key: "all",      label: "Tümü"        },
  { key: "open",     label: "Açık"        },
  { key: "priced",   label: "Fiyatlandı"  },
  { key: "accepted", label: "Kabul"       },
  { key: "rejected", label: "Reddedildi"  },
  { key: "expired",  label: "Süresi Doldu" }
];

export default function Quotes() {
  const [tab, setTab] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [priceDialog, setPriceDialog] = useState({ open: false, quote: null });
  const [viewDialog,  setViewDialog]  = useState({ open: false, quote: null });
  const [pricingItems, setPricingItems] = useState([]);
  const [validUntil, setValidUntil] = useState("");
  const [adminResponse, setAdminResponse] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get("/api/admin/b2b/quotes");
      setQuotes(Array.isArray(r.data) ? r.data : (r.data?.quotes || []));
    } catch (e) {
      toast.error("Teklifler yüklenemedi: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = quotes.filter(q => {
    if (TABS[tab].key === "all") return true;
    return (q.status || "open") === TABS[tab].key;
  });

  const openPriceDialog = (q) => {
    const items = (q.items || []).map(it => ({
      ...it,
      quoted_price: it.quoted_price ?? it.unit_price ?? 0
    }));
    setPricingItems(items);
    const def = new Date(); def.setDate(def.getDate() + 7);
    setValidUntil(q.valid_until ? q.valid_until.substring(0, 10) : def.toISOString().substring(0, 10));
    setAdminResponse(q.admin_response || "");
    setPriceDialog({ open: true, quote: q });
  };

  const itemTotal = (it) => {
    const qty = Number(it.quantity || 0);
    const price = Number(it.quoted_price || 0);
    return qty * price;
  };
  const grandTotal = pricingItems.reduce((s, it) => s + itemTotal(it), 0);

  const submitPrice = async () => {
    const q = priceDialog.quote;
    if (!q) return;
    setSaving(true);
    try {
      await API.put(`/api/admin/b2b/quotes/${q.id}`, {
        status: "priced",
        items: pricingItems.map(it => ({
          id: it.id,
          product_id: it.product_id,
          quantity: Number(it.quantity),
          quoted_price: Number(it.quoted_price)
        })),
        total_quoted: grandTotal,
        valid_until: validUntil || null,
        admin_response: adminResponse
      });
      toast.success("Teklif fiyatlandı");
      setPriceDialog({ open: false, quote: null });
      load();
    } catch (e) {
      toast.error("Kayıt başarısız: " + (e?.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const rejectQuote = async (q) => {
    if (!window.confirm(`Teklif #${q.id} reddedilsin mi?`)) return;
    try {
      await API.post(`/api/admin/b2b/quotes/${q.id}/reject`, { reason: "Admin tarafından reddedildi" });
      toast.success("Teklif reddedildi");
      load();
    } catch (e) {
      toast.error("İşlem başarısız: " + (e?.response?.data?.error || e.message));
    }
  };

  const counts = TABS.reduce((acc, t) => {
    acc[t.key] = t.key === "all" ? quotes.length : quotes.filter(q => (q.status || "open") === t.key).length;
    return acc;
  }, {});

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>📋 Teklif (RFQ) Yönetimi</Typography>
        <Button variant="outlined" onClick={load} disabled={loading}>Yenile</Button>
      </Stack>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((t, i) => (
            <Tab key={t.key} label={`${t.label} (${counts[t.key] || 0})`} />
          ))}
        </Tabs>

        <CardContent>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Alert severity="info">Bu kategoride teklif bulunmuyor.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell align="center">Ürün</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                  <TableCell align="center">Durum</TableCell>
                  <TableCell>Talep Tarihi</TableCell>
                  <TableCell>Geçerlilik</TableCell>
                  <TableCell align="center">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(q => {
                  const st = STATUS_LABELS[q.status || "open"] || STATUS_LABELS.open;
                  const itemCount = (q.items || []).length;
                  const total = q.total_quoted || (q.items || []).reduce((s, it) => s + (Number(it.quoted_price || it.unit_price || 0) * Number(it.quantity || 0)), 0);
                  return (
                    <TableRow key={q.id} hover>
                      <TableCell>{q.id}</TableCell>
                      <TableCell>{q.customer_name || q.user_name || "—"}</TableCell>
                      <TableCell>{q.customer_company || q.company || "—"}</TableCell>
                      <TableCell align="center">{itemCount}</TableCell>
                      <TableCell align="right">{total > 0 ? trMoney(total) : "—"}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={st.text} color={st.color} />
                      </TableCell>
                      <TableCell>{trDateTime(q.created_at)}</TableCell>
                      <TableCell>{trDate(q.valid_until)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Detay">
                          <IconButton size="small" onClick={() => setViewDialog({ open: true, quote: q })}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(q.status === "open" || q.status === "priced" || !q.status) && (
                          <Tooltip title="Fiyatla">
                            <IconButton size="small" color="primary" onClick={() => openPriceDialog(q)}>
                              <LocalOffer fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Faz AP: PDF görüntüle — auth header ile fetch, blob URL aç */}
                        <Tooltip title="PDF olarak indir / yazdır">
                          <IconButton
                            size="small"
                            sx={{ color: "#dc2626" }}
                            onClick={async () => {
                              // Auth header için fetch + blob URL yaklaşımı
                              // window.open(url) header gönderemez, bu yüzden:
                              // 1. fetch ile HTML çek (auth header'lı)
                              // 2. blob → object URL → yeni sekme aç
                              try {
                                const token = localStorage.getItem("token");
                                const res = await fetch(
                                  `https://api.miksanplastik.com.tr/api/admin/b2b/quotes/${q.id}/pdf`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (!res.ok) {
                                  alert("PDF yüklenemedi: " + res.status);
                                  return;
                                }
                                const html = await res.text();
                                const blob = new Blob([html], { type: "text/html" });
                                const blobUrl = URL.createObjectURL(blob);
                                window.open(blobUrl, "_blank");
                                // Memory cleanup — 60sn sonra blob URL'i serbest bırak
                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                              } catch (e) {
                                alert("PDF açılamadı: " + e.message);
                              }
                            }}
                          >
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(q.status === "open" || q.status === "priced" || !q.status) && (
                          <Tooltip title="Reddet">
                            <IconButton size="small" color="error" onClick={() => rejectQuote(q)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pricing Dialog */}
      <Dialog open={priceDialog.open} onClose={() => setPriceDialog({ open: false, quote: null })} maxWidth="md" fullWidth>
        <DialogTitle>Teklif Fiyatlandır — #{priceDialog.quote?.id}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Müşteri</Typography>
              <Typography>{priceDialog.quote?.customer_name || priceDialog.quote?.user_name || "—"} · {priceDialog.quote?.customer_company || priceDialog.quote?.company || "—"}</Typography>
            </Box>
            {priceDialog.quote?.notes && (
              <Alert severity="info">Müşteri notu: {priceDialog.quote.notes}</Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ürün</TableCell>
                  <TableCell align="center">Miktar</TableCell>
                  <TableCell align="right">Birim Fiyat (₺)</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingItems.map((it, i) => (
                  <TableRow key={i}>
                    <TableCell>{it.product_name || it.name || `Ürün #${it.product_id}`}</TableCell>
                    <TableCell align="center">{it.quantity}</TableCell>
                    <TableCell align="right" sx={{ width: 160 }}>
                      <TextField
                        type="number"
                        size="small"
                        value={it.quoted_price}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPricingItems(items => items.map((x, idx) => idx === i ? { ...x, quoted_price: v } : x));
                        }}
                        inputProps={{ step: "0.01", min: 0, style: { textAlign: "right" } }}
                      />
                    </TableCell>
                    <TableCell align="right">{trMoney(itemTotal(it))}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>Toplam:</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 16 }}>{trMoney(grandTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <TextField
              label="Geçerlilik Tarihi"
              type="date"
              value={validUntil}
              onChange={e => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Müşteriye Not / Açıklama"
              value={adminResponse}
              onChange={e => setAdminResponse(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Ödeme koşulları, teslim süresi, vb."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialog({ open: false, quote: null })}>İptal</Button>
          <Button variant="contained" onClick={submitPrice} disabled={saving} startIcon={<LocalOffer />}>
            {saving ? "Kaydediliyor..." : "Fiyatlandır ve Gönder"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, quote: null })} maxWidth="md" fullWidth>
        <DialogTitle>Teklif Detayı — #{viewDialog.quote?.id}</DialogTitle>
        <DialogContent dividers>
          {viewDialog.quote && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Müşteri</Typography>
                <Typography>{viewDialog.quote.customer_name || viewDialog.quote.user_name || "—"} · {viewDialog.quote.customer_company || viewDialog.quote.company || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Durum / Tutar</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip label={(STATUS_LABELS[viewDialog.quote.status || "open"] || STATUS_LABELS.open).text} color={(STATUS_LABELS[viewDialog.quote.status || "open"] || STATUS_LABELS.open).color} size="small" />
                  <Typography fontWeight={700}>{trMoney(viewDialog.quote.total_quoted || 0)}</Typography>
                </Stack>
              </Box>
              {viewDialog.quote.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Müşteri Notu</Typography>
                  <Typography>{viewDialog.quote.notes}</Typography>
                </Box>
              )}
              {viewDialog.quote.admin_response && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Admin Cevabı</Typography>
                  <Typography>{viewDialog.quote.admin_response}</Typography>
                </Box>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ürün</TableCell>
                    <TableCell align="center">Miktar</TableCell>
                    <TableCell align="right">Fiyat</TableCell>
                    <TableCell align="right">Tutar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(viewDialog.quote.items || []).map((it, i) => {
                    const price = Number(it.quoted_price || it.unit_price || 0);
                    return (
                      <TableRow key={i}>
                        <TableCell>{it.product_name || it.name || `Ürün #${it.product_id}`}</TableCell>
                        <TableCell align="center">{it.quantity}</TableCell>
                        <TableCell align="right">{price > 0 ? trMoney(price) : "—"}</TableCell>
                        <TableCell align="right">{price > 0 ? trMoney(price * Number(it.quantity || 0)) : "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, quote: null })}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
